function splitHash(location) {
  const hashIndex = location.indexOf("#");
  if (hashIndex === -1) {
    return [location, ""];
  }
  return [location.slice(0, hashIndex), location.slice(hashIndex)];
}

/**
 * Appends the incoming request's query string (gclid, utm_*, ...) to the redirect
 * target so ad and analytics attribution survives the 301. Params the target
 * already carries win; the merged query is placed before the hash.
 */
export function withRequestQuery(location, requestUrl) {
  if (!requestUrl || !requestUrl.includes("?")) {
    return location;
  }

  const incomingQuery = new URL(requestUrl, "http://localhost").searchParams;
  const [locationWithoutHash, hash] = splitHash(location);
  const [path, targetQueryString = ""] = locationWithoutHash.split("?");
  const mergedQuery = new URLSearchParams(targetQueryString);

  incomingQuery.forEach((value, key) => {
    if (!mergedQuery.has(key)) {
      mergedQuery.append(key, value);
    }
  });

  const mergedQueryString = mergedQuery.toString();
  return `${path}${mergedQueryString ? `?${mergedQueryString}` : ""}${hash}`;
}

/**
 * Performs a 301 permanent redirect with a canonical Link header. The hash part
 * of the location is left out of the canonical, matching the rel=canonical the
 * target page itself declares. The canonical never includes the incoming
 * request's query string, only the Location header does.
 *
 * Inside a Nuxt context (asyncData/middleware): redirects via context.redirect,
 * which flags the response so the renderer stops; the Link header is set first
 * and merged into Nuxt's redirect response.
 * In pure server middleware (no Nuxt context): writes the 301 to the response
 * directly, ending the connect chain before Nuxt renders.
 *
 * @param {object} options
 * @param {object} [options.res] - Node.js response object (server middleware or context.res)
 * @param {Function} [options.redirect] - Nuxt context.redirect function; when present, redirect goes through it
 * @param {string} options.location - The redirect target path
 * @param {string} [options.baseUrl] - Base URL for canonical header (e.g. "https://joszaki.hu"). Falls back to process.env.TOP_DOMAIN.
 * @param {string} [options.requestUrl] - The incoming request URL whose query string is carried over. Falls back to res.req.url.
 * @param {boolean} [options.keepRequestQuery=true] - Set to false when the location already encodes the query it should have.
 */
export function permanentRedirect({
  res,
  redirect,
  location,
  baseUrl,
  requestUrl,
  keepRequestQuery = true,
}) {
  const origin = baseUrl || process.env.TOP_DOMAIN || "";
  const [locationWithoutHash] = splitHash(location);
  const canonicalUrl = `${origin}${locationWithoutHash}`;
  const linkHeader = `<${canonicalUrl}>; rel="canonical"`;

  const targetLocation = keepRequestQuery
    ? withRequestQuery(location, requestUrl ?? res?.req?.url)
    : location;

  // Inside Nuxt (asyncData/middleware) we must redirect via context.redirect:
  // it flags the response as redirected so the renderer stops. Ending the
  // response ourselves leaves the renderer running, which then throws
  // "Cannot set headers after they are sent" when it writes its CSP/ETag
  // headers. The Link header is set first; Nuxt's writeHead() merges it in.
  if (redirect) {
    if (res && !res.headersSent) {
      res.setHeader("Link", linkHeader);
    }
    redirect(301, targetLocation);
    return;
  }

  // Pure server middleware (no Nuxt context): writing the response directly is
  // correct here — ending it stops the connect chain before Nuxt renders.
  if (res && !res.headersSent) {
    res.writeHead(301, {
      Location: targetLocation,
      Link: linkHeader,
    });
    res.end();
  }
}

export function redirectHomeOnMissing(context) {
  permanentRedirect({
    res: context.res,
    redirect: context.redirect,
    location: "/",
    requestUrl: context.route?.fullPath,
  });
}
